import api from '../httpClient';
import {
  AuthResponse,
  ApiMessageResponse,
  ConsentStatusResponse,
  ForgotPasswordRequestOtpRequest,
  ForgotPasswordResetRequest,
  ForgotPasswordVerifyOtpRequest,
  ForgotPasswordVerifyOtpResponse,
  LoginCredentials,
  LoginResendOtpRequest,
  LoginResponse,
  LoginVerifyOtpRequest,
  RegisterRequest,
  ResendVerificationOtpRequest,
  AcceptFounderAgreementRequest,
  AcceptFounderAgreementResponse,
  FounderAgreementStatusResponse,
  UpdateConsentRequest,
  UpdateConsentResponse,
  User,
  NutritionistProfileResponse,
  VerifyEmailOtpRequest,
  AuthSubscriptionStatusResponse,
} from '@/types/auth';
import type {
  AIMealPlanCohort,
  AIMealPlanCohortPayload,
} from '@/constants/ai-model-options';
import {
  ConsentGateReason,
  clearConsentGateState,
  persistConsentGateState,
  readConsentGateState,
} from '@/lib/consentGate';
import { getDefaultAuthenticatedRoute } from '@/lib/accessControl';
import { clearAppQueryCache } from '@/lib/queryClient';
import { REGISTRATION_NOT_ALLOWED_CODE } from '@/lib/auth/registrationGate';
import {
  clearStoredAuthSession,
  USER_PROFILE_UPDATED_EVENT,
  USER_STORAGE_KEY,
} from '@/lib/authSession';
import {
  parseAuthSubscriptionStatusResponse,
  syncSubscriptionGateFromAuthStatus,
  syncSubscriptionGateFromStatusResponse,
} from '@/lib/subscriptionGate';
import {
  clearFounderAgreementGateState,
  parseFounderAgreementStatusResponse,
  syncFounderAgreementGateFromAuthStatus,
  syncFounderAgreementGateFromStatusResponse,
} from '@/lib/founderAgreementGate';
import {
  identifyAuthenticatedUser,
  trackLoginCompleted,
  trackLoginOtpRequested,
  trackLogoutCompleted,
  trackOAuthAuthCompleted,
  trackSignupCompleted,
  trackSignupStarted,
  trackSignupSubmitted,
} from '@/lib/analytics/trackers/auth';

export {
  clearStoredAuthSession,
  USER_PROFILE_UPDATED_EVENT,
  USER_STORAGE_KEY,
} from '@/lib/authSession';

type StoredUser = Partial<User> & Pick<User, 'id' | 'email' | 'role'> & {
  token: string;
  [key: string]: unknown;
};

const AUTH_STORAGE_OMITTED_USER_FIELDS = [
  'accountType',
  'experienceYears',
  'language',
  'onboardingCompletedAt',
  'phone',
  'phoneCountryCode',
  'practiceAddress',
  'practiceName',
  'preferredMeasurementSystem',
  'professionalBio',
  'pronouns',
  'qualification',
  'registrationBody',
  'registrationExpiryDate',
  'registrationNumber',
  'specialization',
  'timeFormat',
  'timezone',
  'zipCode',
] as const;

const sanitizeStoredUser = (userData: Record<string, unknown>): StoredUser => {
  const sanitized = { ...userData };
  for (const field of AUTH_STORAGE_OMITTED_USER_FIELDS) {
    delete sanitized[field];
  }
  return sanitized as StoredUser;
};

export const persistStoredAuthUser = (userData: Record<string, unknown> & { token: string }): StoredUser => {
  const sanitized = sanitizeStoredUser(userData);
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(sanitized));
  return sanitized;
};

export interface NutritionistProfilePreferencesPersonalDetails {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  pronouns?: string;
}

export interface NutritionistProfilePreferencesContactInfo {
  email?: string;
  phone?: string;
  phoneCountryCode?: string;
  country?: string;
  zipCode?: string | null;
  linkedInAccount?: string | null;
  instagramAccount?: string | null;
}

export interface NutritionistProfilePreferencesProfessionalDetails {
  professionalBio?: string;
  primaryQualification?: string;
  specialization?: string | string[];
  specializations?: string[];
  registrationNumber?: string;
  registrationExpiry?: string;
  registrationExpiryDate?: string;
}

export interface NutritionistProfilePreferencesProfessionalBio {
  bio?: string;
}

export interface NutritionistProfilePreferencesCredentials {
  primaryQualification?: string;
  registrationBody?: string | null;
  registrationNumber?: string;
  registrationExpiry?: string;
  experienceYears?: number | string | null;
  specializations?: string[];
}

export interface NutritionistProfilePreferences {
  menuTutorial?: boolean;
  personalDetails?: NutritionistProfilePreferencesPersonalDetails;
  contactInfo?: NutritionistProfilePreferencesContactInfo;
  professionalBio?: NutritionistProfilePreferencesProfessionalBio | string;
  credentials?: NutritionistProfilePreferencesCredentials;
  professionalDetails?: NutritionistProfilePreferencesProfessionalDetails;
  profileImageUrl?: string | null;
  experienceYears?: number | string | null;
  [key: string]: unknown;
}

export interface InvoiceSettings {
  paymentDueDays: number;
  showLogoOnInvoice: boolean;
  reminderBeforeDueEnabled: boolean;
  reminderBeforeDueDays: number;
  overdueReminderEnabled: boolean;
  chargeVat: boolean;
  vatRate: number;
  vatNumber: string;
}

export type InvoiceSettingsPayload = Partial<InvoiceSettings>;

export type InvoiceSettingsResponse = InvoiceSettingsPayload & Record<string, unknown>;

export interface NutritionistInvoiceProfilePayload {
  currency: string;
  practiceName?: string;
}

export type NutritionistProfilePreferencesContactInfoPayload = Omit<
  NutritionistProfilePreferencesContactInfo,
  'email'
>;

export interface NutritionistProfilePreferencesPayload {
  menuTutorial?: boolean;
  profileImageUrl?: string | null;
  personalDetails?: NutritionistProfilePreferencesPersonalDetails;
  contactInfo?: NutritionistProfilePreferencesContactInfoPayload;
  professionalBio?: {
    bio?: string;
  };
  credentials?: {
    primaryQualification?: string;
    registrationBody?: string | null;
    registrationNumber?: string;
    registrationExpiry?: string;
    experienceYears?: number | null;
    specializations?: string[];
  };
}

export interface ProfilePhotoUploadUrlRequest {
  fileName: string;
  mimeType: string;
  assetType: 'profileImage';
}

export interface ClinicLogoUploadUrlRequest {
  fileName: string;
  mimeType: string;
  assetType: 'clinicLogo';
  fileSize: number;
}

// A data URL expands source bytes by 4/3. This leaves 8 KiB for the response
// envelope under Lambda's 6 MiB buffered-response limit.
export const MAX_CLINIC_LOGO_FILE_BYTES = 4_712_448;

export interface ProfilePhotoUploadUrlPayload {
  method?: string;
  uploadUrl: string;
  fileUrl?: string;
  publicUrl?: string;
  assetUrl?: string;
  url?: string;
  requiredHeaders?: Record<string, string>;
  expiresInSeconds?: number;
}

export interface NutritionistPracticeInfoPayload {
  practiceName: string;
  practiceAddress: string;
  timezone: string;
  country: string;
  zipCode: string;
  clinicLogoUrl?: string;
  currency?: string;
}

const normalizePracticeInfoPayload = (
  payload: NutritionistPracticeInfoPayload
): Partial<NutritionistPracticeInfoPayload> => {
  const normalizedPracticeAddress = payload.practiceAddress.trim();
  const normalizedTimezone = payload.timezone.trim();
  const normalizedCountry = payload.country.trim();
  const normalizedZipCode = payload.zipCode.trim();
  const normalizedClinicLogoUrl = payload.clinicLogoUrl?.trim();
  const normalizedCurrency = payload.currency?.trim();

  return {
    practiceName: payload.practiceName,
    ...(normalizedPracticeAddress ? { practiceAddress: normalizedPracticeAddress } : {}),
    ...(normalizedTimezone ? { timezone: normalizedTimezone } : {}),
    ...(normalizedCountry ? { country: normalizedCountry } : {}),
    ...(normalizedZipCode ? { zipCode: normalizedZipCode } : {}),
    ...(normalizedClinicLogoUrl ? { clinicLogoUrl: normalizedClinicLogoUrl } : {}),
    ...(normalizedCurrency ? { currency: normalizedCurrency } : {}),
  };
};

export interface PreProvisionedRoleAssignment {
  email: string;
  roles: string[];
}

export interface PreProvisionedRolesUpsertResponse {
  success: boolean;
  message: string;
  data: {
    count: number;
    items: Array<{
      email: string;
      roles: string[];
      existingUserUpdated: boolean;
    }>;
  };
}

export interface AdminUserRoleRecord {
  id: string;
  email: string;
  userType: string;
  linkedProfileId: string | null;
  displayName: string | null;
  roles: string[];
  aiMealPlanCohort?: AIMealPlanCohortPayload;
  disabled: boolean;
  disabledAt: string | null;
  disabledBy: string | null;
  disabledReason: string | null;
  emailVerified: boolean;
  authProvider: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  lastLoginAt: string | null;
}

export interface AIMealPlanOptionsResponse {
  success: boolean;
  data: AIMealPlanCohortPayload;
}

export interface AdminAIMealPlanCohortAssignment {
  email: string;
  cohort: AIMealPlanCohort;
}

export interface AdminAIMealPlanCohortsUpsertResponse {
  success: boolean;
  message: string;
  data: {
    count: number;
    items: Array<{
      email: string;
      assignedCohort: AIMealPlanCohort;
      existingUserUpdated: boolean;
      aiMealPlanCohort: AIMealPlanCohortPayload;
    }>;
  };
}

export interface AdminUserAIMealPlanCohortUpdateResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    email: string;
    updatedAt: string | null;
    aiMealPlanCohort: AIMealPlanCohortPayload;
  };
}

export interface AdminUsersListResponse {
  success: boolean;
  data: {
    count: number;
    allowedRoles: string[];
    items: AdminUserRoleRecord[];
  };
}

export interface AdminUserRolesUpdateResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    email: string;
    roles: string[];
    updatedAt: string | null;
  };
}

export interface AdminUserStatusUpdateResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    email: string;
    disabled: boolean;
    disabledAt: string | null;
    disabledBy: string | null;
    disabledReason: string | null;
    updatedAt: string | null;
  };
}

export type AdminUsageQualityAIUsageAction = 'generate' | 'smart_edit' | 'regenerate';

export interface AdminUsageQualityAIUsageTotals {
  calls: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  cachedPromptTokens: number;
  uncachedPromptTokens: number;
  reasoningTokens: number;
  estimatedCostUsd: number;
  hasUnknownCost: boolean;
}

export interface AdminUsageQualityAIUsageDaily extends AdminUsageQualityAIUsageTotals {
  date: string;
}

export interface AdminUsageQualityAIUsageQuotaAction {
  action: AdminUsageQualityAIUsageAction;
  label: string;
  limit: number;
  used: number;
  remaining: number;
  windowHours: number;
  windowStartedAt: string | null;
  resetAt: string | null;
  retryAfterTimestamp: string | null;
  retryAfterSeconds: number | null;
}

export interface AdminUsageQualityAIUsageQuota {
  actions: AdminUsageQualityAIUsageQuotaAction[];
  totalLimit: number;
  totalUsed: number;
  totalRemaining: number;
  nextResetAt: string | null;
}

export interface AdminUsageQualityAIUsage extends AdminUsageQualityAIUsageTotals {
  daily: AdminUsageQualityAIUsageDaily[];
  quota: AdminUsageQualityAIUsageQuota;
}

export interface AdminUsageQualityActivity {
  id: string;
  type: 'login' | 'app_usage' | 'client_onboarded' | 'meal_plan_generated' | 'meal_plan_finalized' | 'ai_generate' | 'ai_smart_edit' | 'ai_regenerate';
  label: string;
  detail: string | null;
  occurredAt: string;
  source: string;
  outcome: 'observed' | 'attempted' | 'completed';
  resourceType: 'user' | 'client' | 'meal_plan_draft' | 'meal_plan' | null;
  resourceId: string | null;
}

export interface AdminUsageQualityPractitioner {
  nutritionistId: string;
  nutritionistEmail: string | null;
  nutritionistName: string;
  practiceName: string | null;
  country: string | null;
  accountClassification: 'external' | 'internal' | 'test_demo' | 'duplicate';
  classificationReason: string;
  disabled: boolean;
  emailVerified: boolean;
  activated: boolean;
  meaningfullyActive: boolean;
  active: boolean;
  activitySignals: {
    lastLogin: boolean;
    appUsage: boolean;
    clientOnboarding: boolean;
    mealPlanGeneration: boolean;
    finalizedMealPlan: boolean;
  };
  lastLoginAt: string | null;
  lastActivityAt: string | null;
  clientsOnboarded: number;
  clientOnboardings: Array<{
    clientId: string;
    clientLabel: string;
    status: string | null;
    clientCreatedAt: string | null;
    occurredAt: string | null;
    source: 'created' | 'assigned';
  }>;
  mealPlansGenerated: number;
  finalizedMealPlans: number;
  sourceCounts: Record<string, number>;
  aiActionCounts: { generate: number; smart_edit: number; regenerate: number };
  activityCount: number;
  activities: AdminUsageQualityActivity[];
  aiUsage?: AdminUsageQualityAIUsage;
}

export interface AdminUsageQualityMealPreviewSuggestion {
  recipeId: string | null;
  title: string;
  calories: number | null;
  protein: number | null;
  carbs: number | null;
  fat: number | null;
}

export interface AdminUsageQualityMealPreviewItem {
  dayNumber: number;
  mealName: string;
  recipeTitle: string;
  selectedRecipeId?: string | null;
  hasSelectedRecipe?: boolean;
  calories: number | null;
  protein: number | null;
  carbs: number | null;
  fat: number | null;
  recipeSuggestions?: AdminUsageQualityMealPreviewSuggestion[];
}

export interface AdminUsageQualityRecipeNutrition {
  calories: number | null;
  protein: number | null;
  carbs: number | null;
  fat: number | null;
  fibre: number | null;
  sodium: number | null;
}

export interface AdminUsageQualityRecipeDetail {
  recipeId: string | null;
  sourceId: string | null;
  title: string;
  description: string | null;
  image: string | null;
  generatedVia: string | null;
  generatedFromTitle: string | null;
  readyInMinutes: number | null;
  servings: number | null;
  nutritionServings: number | null;
  nutrition: AdminUsageQualityRecipeNutrition;
  ingredients: string[];
  instructions: string[];
  allergens: string[];
  intoleranceTags: string[];
  cuisines: string[];
  mealTypes: string[];
  dietLabels: string[];
  isVegan: boolean;
  isVegetarian: boolean;
  isGlutenFree: boolean;
  isDairyFree: boolean;
  customized: boolean;
}

export interface AdminUsageQualityMealDetail {
  mealOrder: number;
  mealName: string;
  mealType: string;
  mealTime: string;
  targetCalories: number | null;
  notes: string | null;
  selectedRecipeId: string | null;
  selectedRecipe: AdminUsageQualityRecipeDetail | null;
  alternatives: AdminUsageQualityRecipeDetail[];
}

export interface AdminUsageQualityDayDetail {
  dayNumber: number;
  date: string | null;
  notes: string | null;
  totals: AdminUsageQualityRecipeNutrition;
  meals: AdminUsageQualityMealDetail[];
}

export interface AdminUsageQualityMealPlanDetail {
  dayCount: number;
  selectedMealCount: number;
  dailyAverages: AdminUsageQualityRecipeNutrition;
  reviewFlags: {
    missingSelectionCount: number;
    mealsWithAllergenTags: number;
    mealsWithIntoleranceTags: number;
    customizedRecipeCount: number;
    generatedRecipeCount: number;
  };
  days: AdminUsageQualityDayDetail[];
}

export interface AdminUsageQualitySampleMealPlan {
  draftId: string;
  name: string;
  source: string;
  aiModel: string | null;
  status: string | null;
  generationState: 'queued' | 'in_progress' | 'completed' | 'failed' | null;
  days: number | null;
  mealCount: number;
  targetCalories: number | null;
  nutritionistId: string | null;
  nutritionistName: string;
  nutritionistEmail: string | null;
  nutritionistCountry: string | null;
  clientId: string | null;
  clientName: string;
  generatedAt: string | null;
  createdAt: string | null;
  startDate: string | null;
  endDate: string | null;
  medicalConditionsApplied: string[];
  restrictions: {
    allergies: string[];
    intolerances: string[];
    cuisines: string[];
    dietaryPreferences: string[];
  };
  mealPreview: AdminUsageQualityMealPreviewItem[];
  mealPlanDetail?: AdminUsageQualityMealPlanDetail;
}

export interface AdminUsageQualityCohortMember {
  email: string;
  userId: string | null;
  nutritionistId: string | null;
  displayName: string;
  country: string | null;
  assignedCohort: AIMealPlanCohort | null;
  effectiveCohort: AIMealPlanCohort;
  status: 'active' | 'disabled' | 'pre_provisioned' | string;
  source: 'user_auth' | 'pre_provisioned' | string;
  lastLoginAt: string | null;
  updatedAt: string | null;
}

export interface AdminUsageQualityCohortGroup {
  cohort: AIMealPlanCohort;
  count: number;
  members: AdminUsageQualityCohortMember[];
}

export interface AdminUsageQualityWindow {
  start: string;
  end: string;
  requestedDays: number;
  timezone?: string;
  utcDateKeys?: string[];
}

export interface AdminUsageQualitySummary {
  activeNutritionists: number;
  genuinePractitioners?: number;
  activatedPractitioners?: number;
  meaningfullyActivePractitioners?: number;
  accountClassificationCounts?: Record<'external' | 'internal' | 'test_demo' | 'duplicate', number>;
  clientsOnboarded: number;
  mealPlansGenerated: number;
  finalizedMealPlans: number;
  sampleMealPlans: number;
  sampleMealPlansMatching?: number;
  sourceCounts: Record<string, number>;
}

export interface AdminUsageQualityRollingWindow {
  window: {
    start: string;
    end: string;
    hours: number;
  };
  summary: AdminUsageQualitySummary;
  aiUsage?: AdminUsageQualityAIUsage;
}

export interface AdminUsageQualityWeeklyParams {
  from?: string;
  to?: string;
  days?: number;
  sampleLimit?: number;
  search?: string;
  source?: string;
  nutritionistId?: string;
}

export interface AdminUsageQualityWeeklyResponse {
  success: boolean;
  data: {
    window: AdminUsageQualityWindow;
    summary: AdminUsageQualitySummary;
    aiUsage?: AdminUsageQualityAIUsage;
    last24Hours?: AdminUsageQualityRollingWindow;
    practitioners: AdminUsageQualityPractitioner[];
    sampleMealPlans: AdminUsageQualitySampleMealPlan[];
    cohorts: AdminUsageQualityCohortGroup[];
  };
}

export interface AdminLandingOperationsWindow {
  start: string;
  end: string;
  requestedDays: number;
  timezone: string;
}

export interface AdminLandingOperationsSummary {
  totalVisits: number;
  aggregateBuckets: number;
  countries: number;
  cities: number;
  networkBuckets: number;
  lastSeenAt: string | null;
  rawIpStored: boolean;
  serverSideEvents?: number;
  serverSideEventNames?: string[];
  serverSideLastSeenAt?: string | null;
}

export interface AdminLandingOperationsLocation {
  country: string | null;
  region: string | null;
  city: string | null;
  count: number;
  networkBuckets: number;
  lastSeenAt: string | null;
}

export interface AdminLandingOperationsDimension {
  value: string | null;
  count: number;
  lastSeenAt: string | null;
}

export interface AdminLandingOperationsBucket {
  id: string;
  day: string;
  pagePath: string;
  referrerHost: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  timezone: string | null;
  language: string | null;
  deviceType: string;
  browserFamily: string;
  ipNetworkHash: string | null;
  count: number;
  updatedAt: string | null;
}

export interface AdminLandingOperationsServerEvent {
  id: string;
  eventName: string;
  eventId: string | null;
  eventSourceUrl: string | null;
  referrer: string | null;
  clientIp: string | null;
  attribution: Record<string, unknown> | null;
  customData: Record<string, unknown> | null;
  createdAt: string | null;
}

export interface AdminLandingOperationsParams {
  from?: string;
  to?: string;
  days?: number;
  limit?: number;
}

export interface AdminLandingOperationsResponse {
  success: boolean;
  data: {
    window: AdminLandingOperationsWindow;
    summary: AdminLandingOperationsSummary;
    locations: AdminLandingOperationsLocation[];
    pages: AdminLandingOperationsDimension[];
    referrers: AdminLandingOperationsDimension[];
    devices: AdminLandingOperationsDimension[];
    browsers: AdminLandingOperationsDimension[];
    recentBuckets: AdminLandingOperationsBucket[];
    serverEvents?: AdminLandingOperationsServerEvent[];
  };
}

const normalizeConsentReason = (reason: unknown): ConsentGateReason => {
  if (reason === 'missing' || reason === 'revoked') {
    return reason;
  }
  return null;
};

const syncConsentGateFromAuthResponse = (
  responseData: Partial<AuthResponse> | null | undefined,
  returnTo: string | null = getDefaultAuthenticatedRoute(responseData?.user ?? null)
) => {
  const consent = responseData?.consent;
  if (!consent) {
    return;
  }

  if (consent.accessBlocked) {
    persistConsentGateState({
      accessBlocked: true,
      reason: normalizeConsentReason(consent.reason),
      returnTo,
    });
    return;
  }

  clearConsentGateState();
};

const syncSubscriptionGateFromAuthResponse = (
  responseData: Partial<AuthResponse> | null | undefined,
  returnTo: string | null = getDefaultAuthenticatedRoute(responseData?.user ?? null),
) => {
  syncSubscriptionGateFromAuthStatus(responseData?.subscription, returnTo);
};

const syncFounderAgreementGateFromAuthResponse = (
  responseData: Partial<AuthResponse> | null | undefined,
  returnTo: string | null = getDefaultAuthenticatedRoute(responseData?.user ?? null),
) => {
  syncFounderAgreementGateFromAuthStatus(responseData?.founderAgreement, returnTo);
};

const persistAuthSession = (responseData: AuthResponse) => {
  const previousUserId = getCurrentUser()?.id;
  const userData: StoredUser = { ...responseData.user, token: responseData.token };
  persistStoredAuthUser(userData);
  identifyAuthenticatedUser(userData);
  if (!previousUserId || previousUserId !== responseData.user.id) {
    clearAppQueryCache();
  }
  const returnTo = getDefaultAuthenticatedRoute(responseData.user);
  syncConsentGateFromAuthResponse(responseData, returnTo);
  syncSubscriptionGateFromAuthResponse(responseData, returnTo);
  syncFounderAgreementGateFromAuthResponse(responseData, returnTo);
};

const asLooseRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const readNestedRecord = (
  record: Record<string, unknown>,
  keys: string[],
): Record<string, unknown> | null => {
  for (const key of keys) {
    const nested = asLooseRecord(record[key]);
    if (nested) {
      return nested;
    }
  }
  return null;
};

const readProfileImageUrlFromPreferences = (
  profilePreferences: NutritionistProfilePreferences | null | undefined,
): string => {
  const responseRoot = asLooseRecord(profilePreferences);
  if (!responseRoot) return '';

  const profileRoot = asLooseRecord(responseRoot.data) || responseRoot;
  const personalDetails =
    readNestedRecord(profileRoot, ['personalDetails', 'personal', 'profile'])
    || profileRoot;

  return readFirstString(
    profileRoot.profileImageUrl,
    profileRoot.profilePhotoUrl,
    profileRoot.photoUrl,
    profileRoot.avatarUrl,
    personalDetails.profileImageUrl,
    personalDetails.profilePhotoUrl,
    personalDetails.photoUrl,
    personalDetails.avatarUrl,
  );
};

const hydrateStoredUserProfilePreferences = async (): Promise<StoredUser | null> => {
  const currentUser = getCurrentUser();
  if (!currentUser?.token) {
    return currentUser;
  }

  // Profile endpoints are consent-protected. Login responses persist the gate
  // before hydration, so do not generate an expected 403 while the user is
  // being redirected to the consent screen.
  if (readConsentGateState()?.accessBlocked) {
    return currentUser;
  }

  try {
    const profilePreferences = await getNutritionistProfilePreferences();
    const profileImageUrl = readProfileImageUrlFromPreferences(profilePreferences);
    if (!profileImageUrl) {
      return getCurrentUser();
    }
    return updateStoredUserProfile({ profileImageUrl });
  } catch (error) {
    console.warn('Failed to hydrate profile preferences after login', error);
    return getCurrentUser();
  }
};

const isAuthenticatedLoginResponse = (responseData: LoginResponse): responseData is AuthResponse => {
  return (
    !('requiresMfa' in responseData && responseData.requiresMfa)
    && typeof (responseData as AuthResponse).token === 'string'
    && Boolean((responseData as AuthResponse).user)
  );
};

const readUserFromProfilePayload = (payload: unknown): User | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const payloadRecord = payload as Record<string, unknown>;
  const candidate =
    (payloadRecord.data && typeof payloadRecord.data === 'object' && payloadRecord.data) ||
    (payloadRecord.user && typeof payloadRecord.user === 'object' && payloadRecord.user) ||
    payloadRecord;

  if (!candidate || typeof candidate !== 'object') {
    return null;
  }

  const user = candidate as Partial<User>;
  if (typeof user.id !== 'string' || typeof user.email !== 'string') {
    return null;
  }

  return user as User;
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const tokenParts = token.split('.');
  if (tokenParts.length !== 3) {
    return null;
  }

  try {
    const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = atob(padded);
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const buildFallbackUserFromToken = (token: string): User | null => {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return null;
  }

  const nowIso = new Date().toISOString();
  const id = typeof payload.id === 'string' ? payload.id : '';
  const email = typeof payload.email === 'string' ? payload.email : '';
  const fallbackFullName = email.includes('@') ? email.split('@')[0] : 'Nutritionist';

  if (!id || !email) {
    return null;
  }

  const tokenRoles = Array.isArray(payload.roles)
    ? payload.roles.map((role) => String(role || '').trim()).filter(Boolean)
    : [];
  const tokenRole = typeof payload.role === 'string' ? payload.role.trim() : '';
  const roles = tokenRoles.length
    ? Array.from(new Set(tokenRoles))
    : tokenRole
      ? [tokenRole]
      : ['nutritionist'];

  return {
    id,
    schemaVersion: 2,
    createdAt: nowIso,
    updatedAt: nowIso,
    email,
    fullName: fallbackFullName,
    role: roles[0] ?? 'nutritionist',
    roles,
    preferredMeasurementSystem: 'metric',
  };
};

export interface OAuthHashSessionResult {
  handled: boolean;
  authenticated?: boolean;
  memberStatus?: 'new' | 'existing';
  provider?: string;
  state?: string;
  consentRequired?: boolean;
  consentReason?: ConsentGateReason;
  registrationBlocked?: boolean;
}

export const hasOAuthHashSession = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;

  if (!hash) {
    return false;
  }

  return Boolean(new URLSearchParams(hash).get('token')?.trim());
};

export const persistTokenAuthSession = async (
  token: string,
  authProvider?: string,
  options: { skipProtectedHydration?: boolean } = {},
): Promise<User | null> => {
  let resolvedUser: User | null = null;
  const normalizedAuthProvider = authProvider?.trim().toLowerCase();

  if (!options.skipProtectedHydration) {
    try {
      const profileResponse = await api.get('/nutritionists/profile', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      resolvedUser = readUserFromProfilePayload(profileResponse.data);
    } catch (error) {
      console.warn('Failed to hydrate profile from token, falling back to token payload', error);
    }
  }

  if (!resolvedUser) {
    resolvedUser = buildFallbackUserFromToken(token);
  }

  if (!resolvedUser) {
    return null;
  }

  const userData: StoredUser = {
    ...resolvedUser,
    ...(normalizedAuthProvider ? { authProvider: normalizedAuthProvider } : {}),
    token,
  };
  persistStoredAuthUser(userData);
  const hydratedUser = options.skipProtectedHydration
    ? userData
    : await hydrateStoredUserProfilePreferences();
  identifyAuthenticatedUser(hydratedUser ?? userData);
  return hydratedUser ?? userData;
};

export const consumeOAuthHashSession = async (): Promise<OAuthHashSessionResult> => {
  if (typeof window === 'undefined') {
    return { handled: false };
  }

  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;

  if (!hash) {
    return { handled: false };
  }

  const fragmentParams = new URLSearchParams(hash);
  const token = fragmentParams.get('token')?.trim();
  const registrationBlockedCode =
    fragmentParams.get('code')?.trim()
    || fragmentParams.get('errorCode')?.trim()
    || fragmentParams.get('error')?.trim();

  if (!token) {
    if (registrationBlockedCode === REGISTRATION_NOT_ALLOWED_CODE) {
      window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`);
      return {
        handled: true,
        authenticated: false,
        registrationBlocked: true,
      };
    }

    return { handled: false };
  }

  const memberStatusRaw = fragmentParams.get('memberStatus')?.trim().toLowerCase();
  const memberStatus =
    memberStatusRaw === 'new' || memberStatusRaw === 'existing'
      ? (memberStatusRaw as 'new' | 'existing')
      : undefined;
  const state = fragmentParams.get('state')?.trim() || undefined;
  const consentRequired =
    fragmentParams.get('consentRequired')?.trim().toLowerCase() === 'true';
  const consentReason = normalizeConsentReason(fragmentParams.get('consentReason'));
  const provider = fragmentParams.get('provider')?.trim() || undefined;
  const authProvider = fragmentParams.get('authProvider')?.trim() || provider;
  const hydratedUser = await persistTokenAuthSession(token, authProvider, {
    skipProtectedHydration: consentRequired,
  });

  if (consentRequired) {
    persistConsentGateState({
      accessBlocked: true,
      reason: consentReason,
      returnTo: getDefaultAuthenticatedRoute(hydratedUser),
    });
  } else {
    clearConsentGateState();
  }
  clearFounderAgreementGateState();

  // Strip auth fragment from browser URL once consumed.
  window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`);

  if (hydratedUser) {
    identifyAuthenticatedUser(hydratedUser);
    trackOAuthAuthCompleted(memberStatus, {
      auth_method: provider ? `${provider.toLowerCase()}_oauth` : 'oauth',
      auth_provider: provider?.toLowerCase(),
    });
  }

  return {
    handled: true,
    authenticated: Boolean(hydratedUser),
    memberStatus,
    provider,
    state,
    consentRequired,
    consentReason,
  };
};

export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  const response = await api.post<LoginResponse>('/auth/login', credentials);
  if (isAuthenticatedLoginResponse(response.data)) {
    persistAuthSession(response.data);
    const hydratedUser = await hydrateStoredUserProfilePreferences();
    identifyAuthenticatedUser(hydratedUser ?? response.data.user);
    trackLoginCompleted({
      auth_method: 'password',
    });
  } else if (response.data.requiresMfa) {
    trackLoginOtpRequested({
      auth_method: 'password',
      otp_channel: 'email',
    });
  }
  return response.data;
};

export const verifyLoginOtp = async (payload: LoginVerifyOtpRequest): Promise<AuthResponse> => {
  const response = await api.post<AuthResponse>('/auth/login/verify-otp', payload);
  persistAuthSession(response.data);
  const hydratedUser = await hydrateStoredUserProfilePreferences();
  identifyAuthenticatedUser(hydratedUser ?? response.data.user);
  trackLoginCompleted({
    auth_method: 'email_otp',
  });
  return response.data;
};

export const resendLoginOtp = async (payload: LoginResendOtpRequest): Promise<ApiMessageResponse> => {
  const response = await api.post('/auth/login/resend-otp', payload);
  return response.data;
};

export const exchangeGoogleOAuthCode = async (payload: {
  code: string;
  redirectUri?: string | null;
  state?: string | null;
  consent?: UpdateConsentRequest | null;
  timezone?: string | null;
}): Promise<AuthResponse & { memberStatus?: 'new' | 'existing' }> => {
  const requestBody = {
    code: payload.code,
    ...(payload.redirectUri ? {
      redirectUri: payload.redirectUri,
      redirect_uri: payload.redirectUri,
    } : {}),
    ...(payload.state ? { state: payload.state } : {}),
    ...(payload.consent ? { consent: payload.consent } : {}),
    ...(payload.timezone ? { timezone: payload.timezone } : {}),
  };
  const response = await api.post<AuthResponse & { memberStatus?: 'new' | 'existing' }>(
    '/auth/google/login',
    requestBody,
  );
  persistAuthSession(response.data);
  const hydratedUser = await hydrateStoredUserProfilePreferences();
  identifyAuthenticatedUser(hydratedUser ?? response.data.user);
  trackOAuthAuthCompleted(response.data.memberStatus, {
    auth_method: 'google_oauth',
    auth_provider: 'google',
  });
  return response.data;
};

// Logout function to clear stored data
export const logout = () => {
  trackLogoutCompleted();
  clearStoredAuthSession();
  window.location.href = '/login';
};

// Get current user from localStorage
export const getCurrentUser = (): StoredUser | null => {
  const primary = localStorage.getItem(USER_STORAGE_KEY);
  
  if (primary) {
    try {
      const parsed = JSON.parse(primary) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        localStorage.removeItem(USER_STORAGE_KEY);
        return null;
      }

      const record = parsed as Record<string, unknown>;
      if (typeof record.token !== 'string' || !record.token) {
        localStorage.removeItem(USER_STORAGE_KEY);
        return null;
      }

      const sanitized = sanitizeStoredUser(record);
      if (JSON.stringify(sanitized) !== primary) {
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(sanitized));
      }

      return sanitized;
    } catch (error) {
      console.error('Failed to parse stored user data', error);
      localStorage.removeItem(USER_STORAGE_KEY);
      return null;
    }
  }

  return null;
};

export const updateStoredUserProfile = (updates: Partial<User>): StoredUser | null => {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    return null;
  }

  const nextUser: StoredUser = {
    ...currentUser,
    ...updates,
    token: currentUser.token,
  };
  const persistedUser = persistStoredAuthUser(nextUser);
  identifyAuthenticatedUser(persistedUser);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(USER_PROFILE_UPDATED_EVENT));
  }
  return persistedUser;
};

const getStoredToken = (): string | null => {
  const user = getCurrentUser();
  if (!user || typeof user.token !== 'string' || !user.token) {
    return null;
  }
  return user.token;
};

const buildAuthHeader = (): Record<string, string> => {
  const token = getStoredToken();
  if (!token) {
    return {};
  }
  return {
    Authorization: `Bearer ${token}`,
  };
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return Boolean(getCurrentUser()?.token);
};

// Update user profile
export const updateUserProfile = async (profileData: Record<string, unknown>) => {
  const response = await api.put('/nutritionists/profile', profileData);
  return response.data;
};

// Register user
export const registerUser = async (registrationData: RegisterRequest): Promise<ApiMessageResponse> => {
  trackSignupStarted({
    account_type: registrationData.accountType,
    auth_method: 'email_password',
    has_practice_name: Boolean(registrationData.practiceName),
    preferred_measurement_system: registrationData.preferredMeasurementSystem,
  });
  const response = await api.post('/auth/register', registrationData);
  trackSignupSubmitted({
    account_type: registrationData.accountType,
    auth_method: 'email_password',
    has_practice_name: Boolean(registrationData.practiceName),
    preferred_measurement_system: registrationData.preferredMeasurementSystem,
  });
  return response.data;
};

export const verifyEmailOtp = async (payload: VerifyEmailOtpRequest): Promise<AuthResponse> => {
  const response = await api.post<AuthResponse>('/auth/verify-email', payload);
  persistAuthSession(response.data);
  const hydratedUser = await hydrateStoredUserProfilePreferences();
  identifyAuthenticatedUser(hydratedUser ?? response.data.user);
  trackSignupCompleted({
    auth_method: 'email_otp',
  });
  return response.data;
};

export const resendVerificationOtp = async (payload: ResendVerificationOtpRequest): Promise<ApiMessageResponse> => {
  const response = await api.post('/auth/resend-verification-otp', payload);
  return response.data;
};

export const requestForgotPasswordOtp = async (payload: ForgotPasswordRequestOtpRequest): Promise<ApiMessageResponse> => {
  const response = await api.post('/auth/forgot-password/request', payload);
  return response.data;
};

export const verifyForgotPasswordOtp = async (
  payload: ForgotPasswordVerifyOtpRequest,
): Promise<ForgotPasswordVerifyOtpResponse> => {
  const response = await api.post<ForgotPasswordVerifyOtpResponse>('/auth/forgot-password/verify-otp', payload);
  return response.data;
};

export const resetForgotPassword = async (payload: ForgotPasswordResetRequest): Promise<ApiMessageResponse> => {
  const response = await api.post('/auth/forgot-password/reset', payload);
  return response.data;
};

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

export const changePassword = async (payload: ChangePasswordRequest): Promise<ApiMessageResponse> => {
  const response = await api.post('/auth/change-password', payload);
  return response.data;
};

export const getConsentStatus = async (): Promise<ConsentStatusResponse> => {
  const response = await api.get<ConsentStatusResponse>('/auth/consent', {
    headers: buildAuthHeader(),
  });
  syncConsentGateFromAuthResponse(
    {
      consent: response.data.consent,
    },
    getDefaultAuthenticatedRoute(getCurrentUser())
  );
  return response.data;
};

export const getAuthSubscription = async (): Promise<AuthSubscriptionStatusResponse> => {
  const response = await api.get('/auth/subscription', {
    headers: buildAuthHeader(),
  });
  const parsed = parseAuthSubscriptionStatusResponse(response.data);
  const payload: AuthSubscriptionStatusResponse = parsed ?? {
    subscription: null,
    enforcementEnabled: false,
  };
  syncSubscriptionGateFromStatusResponse(
    payload,
    getDefaultAuthenticatedRoute(getCurrentUser()),
  );
  return payload;
};

export const getFounderAgreementStatus = async (): Promise<FounderAgreementStatusResponse> => {
  const response = await api.get('/auth/founder-agreement', {
    headers: buildAuthHeader(),
  });
  const parsed = parseFounderAgreementStatusResponse(response.data);
  const payload: FounderAgreementStatusResponse = parsed ?? {
    founderAgreement: {
      allowed: true,
      accessBlocked: false,
      required: false,
      isFounder: false,
      reason: null,
      agreementVersion: 'founding-circle-v1',
      acceptedAt: null,
      acceptedVersion: null,
      cohort: null,
      planCode: null,
    },
  };
  syncFounderAgreementGateFromStatusResponse(
    payload,
    getDefaultAuthenticatedRoute(getCurrentUser()),
  );
  return payload;
};

export const acceptFounderAgreement = async (
  payload: AcceptFounderAgreementRequest,
): Promise<AcceptFounderAgreementResponse> => {
  const response = await api.post<AcceptFounderAgreementResponse>(
    '/auth/founder-agreement',
    payload,
    {
      headers: buildAuthHeader(),
    },
  );
  syncFounderAgreementGateFromStatusResponse(
    {
      founderAgreement: response.data.founderAgreement,
    },
    getDefaultAuthenticatedRoute(getCurrentUser()),
  );
  return response.data;
};

export const updateConsentStatus = async (
  payload: UpdateConsentRequest
): Promise<UpdateConsentResponse> => {
  const response = await api.post<UpdateConsentResponse>('/auth/consent', payload, {
    headers: buildAuthHeader(),
  });
  syncConsentGateFromAuthResponse(
    {
      consent: response.data.consent,
    },
    getDefaultAuthenticatedRoute(getCurrentUser())
  );
  return response.data;
};

// Get nutritionist profile
export const getNutritionistProfile = async (): Promise<NutritionistProfileResponse> => {
  const response = await api.get('/nutritionists/profile');
  return response.data;
};

export const getNutritionistInvoiceSettings = async (): Promise<InvoiceSettingsResponse> => {
  const response = await api.get('/nutritionists/invoice-settings');
  return unwrapApiData<InvoiceSettingsResponse>(response.data);
};

export const updateNutritionistInvoiceSettings = async (
  payload: InvoiceSettingsPayload,
): Promise<InvoiceSettingsResponse> => {
  const response = await api.put('/nutritionists/invoice-settings', payload);
  return unwrapApiData<InvoiceSettingsResponse>(response.data);
};

export const updateNutritionistInvoiceProfile = async (
  payload: NutritionistInvoiceProfilePayload,
): Promise<NutritionistProfileResponse> => {
  const response = await api.put('/nutritionists/profile', payload);
  return response.data;
};

const unwrapApiData = <T,>(payload: unknown): T => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const inner = (payload as { data: unknown }).data;
    // Only unwrap API envelopes; do not treat arbitrary `data` fields (e.g. null) as the payload.
    if (inner !== null && inner !== undefined && typeof inner === 'object') {
      return inner as T;
    }
  }
  return payload as T;
};

const readFirstString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const readClinicLogoUrlFromProfile = (root: Record<string, unknown>): string => {
  const direct = readFirstString(
    root.clinicLogoUrl,
    root.clinicLogo,
    root.practiceLogoUrl,
    root.practiceLogo,
    root.logoUrl,
  );
  if (direct) return direct;

  for (const key of ['practiceInfo', 'practice', 'profile', 'nutritionist', 'user'] as const) {
    const nested = root[key];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const record = nested as Record<string, unknown>;
      const fromNested = readFirstString(
        record.clinicLogoUrl,
        record.clinicLogo,
        record.practiceLogoUrl,
        record.practiceLogo,
        record.logoUrl,
      );
      if (fromNested) return fromNested;
    }
  }
  return '';
};

/** Practice name and clinic logo for meal-plan PDF export headers. */
export function resolveNutritionistPdfBranding(
  profile: Record<string, unknown> | null | undefined,
): { clinicName?: string; clinicLogoUrl?: string } {
  if (!profile || typeof profile !== 'object') return {};
  const clinicName = readFirstString(profile.practiceName) || undefined;
  const clinicLogoUrl = readClinicLogoUrlFromProfile(profile) || undefined;
  return { clinicName, clinicLogoUrl };
}

const resolveUploadedAssetUrl = (payload: ProfilePhotoUploadUrlPayload | null | undefined): string => {
  if (!payload) return '';
  return readFirstString(payload.fileUrl, payload.publicUrl, payload.assetUrl, payload.url);
};

export const getNutritionistProfilePreferences = async (): Promise<NutritionistProfilePreferences> => {
  const response = await api.get('/nutritionists/profile-pref');
  return unwrapApiData<NutritionistProfilePreferences>(response.data);
};

export const saveNutritionistProfilePreferences = async (
  payload: NutritionistProfilePreferencesPayload,
): Promise<void> => {
  await api.put('/nutritionists/profile-pref', payload);
};

export const getNutritionistProfilePhotoUploadUrl = async (
  payload: ProfilePhotoUploadUrlRequest,
): Promise<ProfilePhotoUploadUrlPayload> => {
  const response = await api.post('/nutritionists/profile/upload-url', payload);
  const data = unwrapApiData<ProfilePhotoUploadUrlPayload>(response.data);

  if (!data?.uploadUrl || !resolveUploadedAssetUrl(data)) {
    throw new Error('Profile photo upload URL response was incomplete.');
  }

  return data;
};

export const updateNutritionistProfilePhoto = async (profileImageUrl: string): Promise<unknown> => {
  const response = await api.put('/nutritionists/profile', { profileImageUrl });
  return response.data;
};

export const deleteNutritionistProfilePhoto = async (): Promise<unknown> => {
  const response = await api.delete('/nutritionists/profile/photo');
  return response.data;
};

export const getNutritionistPracticeInfo = async (): Promise<Partial<NutritionistPracticeInfoPayload>> => {
  const response = await api.get('/nutritionists/profile');
  const root = unwrapApiData<Record<string, unknown>>(response.data) || {};
  return {
    practiceName: typeof root.practiceName === 'string' ? root.practiceName : '',
    practiceAddress: typeof root.practiceAddress === 'string' ? root.practiceAddress : '',
    timezone: typeof root.timezone === 'string' ? root.timezone : '',
    country: typeof root.country === 'string' ? root.country : '',
    zipCode: typeof root.zipCode === 'string' ? root.zipCode : '',
    clinicLogoUrl: readClinicLogoUrlFromProfile(root),
  };
};

export const updateNutritionistPracticeInfo = async (
  payload: NutritionistPracticeInfoPayload,
): Promise<unknown> => {
  const response = await api.put('/nutritionists/profile', normalizePracticeInfoPayload(payload));
  return response.data;
};

export const getNutritionistClinicLogoUploadUrl = async (
  payload: ClinicLogoUploadUrlRequest,
): Promise<ProfilePhotoUploadUrlPayload> => {
  const response = await api.post('/nutritionists/profile/upload-url', payload);
  const data = unwrapApiData<ProfilePhotoUploadUrlPayload>(response.data);

  if (!data?.uploadUrl || !resolveUploadedAssetUrl(data)) {
    throw new Error('Clinic logo upload URL response was incomplete.');
  }

  return data;
};

export const updateNutritionistClinicLogo = async (clinicLogoUrl: string): Promise<unknown> => {
  const response = await api.put('/nutritionists/profile', { clinicLogoUrl });
  return response.data;
};

export const upsertPreProvisionedUserRoles = async (
  assignments: PreProvisionedRoleAssignment[],
): Promise<PreProvisionedRolesUpsertResponse> => {
  const response = await api.post<PreProvisionedRolesUpsertResponse>('/auth/pre-provisioned-roles', {
    assignments,
  });
  return response.data;
};

export const listAdminUsers = async (search?: string): Promise<AdminUsersListResponse> => {
  const response = await api.get<AdminUsersListResponse>('/auth/admin/users', {
    params: search?.trim() ? { search: search.trim(), limit: 250 } : { limit: 250 },
  });
  return response.data;
};

export const getAdminUsageQualityWeekly = async (
  params: AdminUsageQualityWeeklyParams = {},
  signal?: AbortSignal,
): Promise<AdminUsageQualityWeeklyResponse> => {
  const response = await api.get<AdminUsageQualityWeeklyResponse>('/auth/admin/usage-quality-weekly', {
    params,
    signal,
  });
  return response.data;
};

export const getAdminLandingOperations = async (
  params: AdminLandingOperationsParams = {},
): Promise<AdminLandingOperationsResponse> => {
  const response = await api.get<AdminLandingOperationsResponse>('/auth/admin/landing-operations', {
    params,
  });
  return response.data;
};

export const getAIMealPlanOptions = async (): Promise<AIMealPlanOptionsResponse> => {
  const response = await api.get<AIMealPlanOptionsResponse>('/auth/ai-meal-plan-options');
  return response.data;
};

export const upsertAdminAIMealPlanCohorts = async (
  assignments: AdminAIMealPlanCohortAssignment[],
): Promise<AdminAIMealPlanCohortsUpsertResponse> => {
  const response = await api.post<AdminAIMealPlanCohortsUpsertResponse>(
    '/auth/admin/ai-meal-plan-cohorts',
    { assignments },
  );
  return response.data;
};

export const updateAdminUserAIMealPlanCohort = async (
  userId: string,
  cohort: AIMealPlanCohort | null,
): Promise<AdminUserAIMealPlanCohortUpdateResponse> => {
  const response = await api.put<AdminUserAIMealPlanCohortUpdateResponse>(
    `/auth/admin/users/${encodeURIComponent(userId)}/ai-meal-plan-cohort`,
    { cohort },
  );
  return response.data;
};

export const updateAdminUserRoles = async (
  userId: string,
  roles: string[],
): Promise<AdminUserRolesUpdateResponse> => {
  const response = await api.put<AdminUserRolesUpdateResponse>(
    `/auth/admin/users/${encodeURIComponent(userId)}/roles`,
    { roles },
  );
  return response.data;
};

export const updateAdminUserStatus = async (
  userId: string,
  disabled: boolean,
): Promise<AdminUserStatusUpdateResponse> => {
  const response = await api.put<AdminUserStatusUpdateResponse>(
    `/auth/admin/users/${encodeURIComponent(userId)}/status`,
    { disabled },
  );
  return response.data;
};

const normalizeProfilePhotoMimeType = (file: File): string => {
  const raw = (file.type || '').toLowerCase();
  if (raw === 'image/jpg' || raw === 'image/pjpeg') return 'image/jpeg';
  if (raw === 'image/jpeg' || raw === 'image/png') return raw;
  const lowerName = file.name.toLowerCase();
  if (lowerName.endsWith('.png')) return 'image/png';
  return 'image/jpeg';
};

const deriveProfilePhotoFileName = (file: File, mimeType: string): string => {
  const rawName = file.name?.trim() || 'profile-photo';
  const base = rawName.replace(/\.[^.]+$/, '') || 'profile-photo';
  if (mimeType === 'image/png') return `${base}.png`;
  return /\.(jpe?g)$/i.test(rawName) ? rawName : `${base}.jpg`;
};

const buildProfilePhotoUploadHeaders = (
  config: ProfilePhotoUploadUrlPayload,
  mimeType: string,
): Headers => {
  const headers = new Headers();
  if (config.requiredHeaders) {
    for (const [key, value] of Object.entries(config.requiredHeaders)) {
      if (typeof value === 'string' && value.length > 0) {
        headers.set(key, value);
      }
    }
  }
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', mimeType);
  }
  return headers;
};

export const uploadNutritionistProfilePhoto = async (file: File): Promise<string> => {
  const mimeType = normalizeProfilePhotoMimeType(file);
  const fileName = deriveProfilePhotoFileName(file, mimeType);
  const uploadConfig = await getNutritionistProfilePhotoUploadUrl({
    fileName,
    mimeType,
    assetType: 'profileImage',
  });

  const uploadResponse = await fetch(uploadConfig.uploadUrl, {
    method: uploadConfig.method || 'PUT',
    headers: buildProfilePhotoUploadHeaders(uploadConfig, mimeType),
    body: file,
  });

  if (!uploadResponse.ok) {
    const body = await uploadResponse.text().catch(() => '');
    throw new Error(
      body
        ? `Profile photo upload failed (${uploadResponse.status}): ${body}`
        : `Profile photo upload failed (${uploadResponse.status}).`
    );
  }

  const fileUrl = resolveUploadedAssetUrl(uploadConfig);
  await updateNutritionistProfilePhoto(fileUrl);
  return fileUrl;
};

export const uploadNutritionistClinicLogo = async (file: File): Promise<string> => {
  const mimeType = normalizeProfilePhotoMimeType(file);
  const fileName = deriveProfilePhotoFileName(file, mimeType);
  const uploadConfig = await getNutritionistClinicLogoUploadUrl({
    fileName,
    mimeType,
    assetType: 'clinicLogo',
    fileSize: file.size,
  });

  const uploadResponse = await fetch(uploadConfig.uploadUrl, {
    method: uploadConfig.method || 'PUT',
    headers: buildProfilePhotoUploadHeaders(uploadConfig, mimeType),
    body: file,
  });

  if (!uploadResponse.ok) {
    const body = await uploadResponse.text().catch(() => '');
    throw new Error(
      body
        ? `Clinic logo upload failed (${uploadResponse.status}): ${body}`
        : `Clinic logo upload failed (${uploadResponse.status}).`
    );
  }

  const fileUrl = resolveUploadedAssetUrl(uploadConfig);
  await updateNutritionistClinicLogo(fileUrl);
  return fileUrl;
};
